/**
 * CareMap viewer roles for optional facility-ad targeting.
 *
 * Distinct from GTG `targetAudience` (`patient` | `provider` | `facility`) in
 * `gtgMapCarousel.js` — do not collapse the two enums.
 *
 * @typedef {'hospital_administrator' | 'quality_safety_officer' | 'ed_director_ops' | 'ems_regional_dispatch' | 'population_health_community_benefit' | 'researcher_academic' | 'patient_consumer' | 'payer_insurer'} CareMapRole
 */

/** @type {readonly CareMapRole[]} */
export const CARE_MAP_ROLES = Object.freeze([
  "hospital_administrator",
  "quality_safety_officer",
  "ed_director_ops",
  "ems_regional_dispatch",
  "population_health_community_benefit",
  "researcher_academic",
  "patient_consumer",
  "payer_insurer",
]);

const CARE_MAP_ROLE_SET = new Set(CARE_MAP_ROLES);

/** Human-readable labels keyed by {@link CARE_MAP_ROLES} — single source for UI copy. */
export const CARE_MAP_ROLE_LABELS = Object.freeze({
  hospital_administrator: "Hospital Administrator",
  quality_safety_officer: "Quality & Patient Safety Officer",
  ed_director_ops: "ED Director / Hospital Ops",
  ems_regional_dispatch: "EMS / Regional Dispatch",
  population_health_community_benefit: "Population Health / Community Benefit",
  researcher_academic: "Researcher / Academic",
  patient_consumer: "Patient / Consumer",
  payer_insurer: "Payer / Insurer",
});

/**
 * @param {unknown} role
 * @returns {string}
 */
export function getCareMapRoleLabel(role) {
  if (typeof role === "string" && CARE_MAP_ROLE_LABELS[role]) {
    return CARE_MAP_ROLE_LABELS[role];
  }
  return typeof role === "string" && role ? role : "";
}

/**
 * Reference-table rows for the 8 CareMap viewer roles, in {@link CARE_MAP_ROLES} order.
 * Codes come from `DEV_AUTH_CODES` entries that set `careMapRole` — not a second role list.
 *
 * @param {Record<string, { careMapRole?: string }>} [authCodes]
 * @returns {Array<{ role: CareMapRole, label: string, code: string | null }>}
 */
export function listCareMapRoleDevAuthRows(authCodes) {
  const byRole = {};
  Object.entries(authCodes || {}).forEach(([code, data]) => {
    if (isCareMapRole(data?.careMapRole) && byRole[data.careMapRole] == null) {
      byRole[data.careMapRole] = code;
    }
  });
  return CARE_MAP_ROLES.map((role) => ({
    role,
    label: getCareMapRoleLabel(role),
    code: byRole[role] || null,
  }));
}

/**
 * Advertiser classification on existing zip listing / facility-ad documents.
 *
 * `third_party` and `house` are reserved for later flows — valid enum values
 * only. Do not add serving, billing, or UI logic for them yet.
 *
 * @typedef {'facility' | 'third_party' | 'house'} AdvertiserType
 */

export const ADVERTISER_TYPE_FACILITY = "facility";

/** Reserved — no create/serve/billing path in this pass. */
export const ADVERTISER_TYPE_THIRD_PARTY = "third_party";

/** Reserved — no create/serve/billing path in this pass. */
export const ADVERTISER_TYPE_HOUSE = "house";

/** @type {readonly AdvertiserType[]} */
export const ADVERTISER_TYPES = Object.freeze([
  ADVERTISER_TYPE_FACILITY,
  ADVERTISER_TYPE_THIRD_PARTY,
  ADVERTISER_TYPE_HOUSE,
]);

/**
 * Optional fields on existing `zips/{zip}/listings/{userId}` (and the
 * `users/{uid}/listings/{zip}` mirror). Missing values preserve pre-role-targeting
 * behavior: show to all CareMap roles; treat advertiser as a facility.
 *
 * - `targetCareMapRoles`: `CareMapRole[] | null`. Absent, `null`, or `[]` means
 *   no role targeting (current carousel behavior).
 * - `advertiserType`: defaults to `'facility'` when absent. `'third_party'` and
 *   `'house'` are reserved and unused.
 *
 * @typedef {object} CareMapListingRoleFields
 * @property {CareMapRole[] | null} [targetCareMapRoles]
 * @property {AdvertiserType} [advertiserType]
 */

/**
 * @param {unknown} role
 * @returns {role is CareMapRole}
 */
export function isCareMapRole(role) {
  return typeof role === "string" && CARE_MAP_ROLE_SET.has(role);
}

/**
 * @param {unknown} value
 * @returns {value is AdvertiserType}
 */
export function isAdvertiserType(value) {
  return (
    value === ADVERTISER_TYPE_FACILITY ||
    value === ADVERTISER_TYPE_THIRD_PARTY ||
    value === ADVERTISER_TYPE_HOUSE
  );
}

/**
 * Read-time default: existing docs without the field are facility ads.
 *
 * @param {unknown} value
 * @returns {AdvertiserType}
 */
export function normalizeAdvertiserType(value) {
  if (isAdvertiserType(value)) return value;
  return ADVERTISER_TYPE_FACILITY;
}
